import React from 'react';
import { AttributeMetadata } from '@amzn/controllershiphub-typescript-client';
import { Box, SpaceBetween } from '@cloudscape-design/components';
import { CommentFieldType, FIELD_CONFIGS, createAuditInfo } from 'src/utils/ControlSummaryUtils';

/**
 * Props for generating column definitions for the bulk edit preview table
 * @interface GetColumnDefinitionsProps
 */
interface GetColumnDefinitionsProps {
  attributesMetadata: AttributeMetadata[] | undefined;
  editData: Record<string, any>;
  controlDatasetKey: string;
  alias: string;
}

/**
 * Generates column definitions for the bulk edit preview table
 *
 * @param {GetColumnDefinitionsProps} props - Props for generating column definitions
 * @param {AttributeMetadata[] | undefined} props.attributesMetadata - Metadata describing the fields
 * @param {Record<string, any>} props.editData - Current edit data state
 * @param {string} props.controlDatasetKey - Control Data Key from metadata
 * @param {string} props.alias - User alias making the changes
 * @returns {Array<any>} Array of column definitions for the preview table
 */
export const getColumnDefinitions = ({
  attributesMetadata,
  editData,
  controlDatasetKey,
  alias,
}: GetColumnDefinitionsProps) => {
  if (!attributesMetadata) return [];

  const editedFields = Object.keys(editData).filter((key) => editData[key] !== undefined);

  const idColumn = {
    id: controlDatasetKey,
    header:
      attributesMetadata?.find((attr) => attr.name === controlDatasetKey)?.displayName ||
      controlDatasetKey?.toUpperCase(),
    cell: (item: Record<string, any>) => item[controlDatasetKey],
    minWidth: 120,
  };

  const changedValueColumns = editedFields.map((field) => {
    const commentConfig = FIELD_CONFIGS.find((cfg) => cfg.sourceColumn === field);
    return {
      id: field,
      header: attributesMetadata?.find((attr) => attr.name === field)?.displayName || field,
      cell: (item: Record<string, any>) => {
        const originalValue = String(item._originalValues?.[field] ?? '');
        const newValue = String(item[field] ?? '');

        if (commentConfig?.type === CommentFieldType.STANDARD) {
          return (
            <SpaceBetween size="xs" direction="vertical">
              <span style={{ textDecoration: 'line-through', color: '#666', opacity: 0.8 }}>
                {originalValue}
              </span>
              <span style={{ fontWeight: 'bold' }}>{newValue}</span>
              <Box variant="awsui-key-label">
                {alias} - Changed {field} to: {newValue}
              </Box>
            </SpaceBetween>
          );
        } else if (commentConfig) {
          const auditPreview = createAuditInfo(alias || 'current-user', newValue);
          return (
            <SpaceBetween size="xs" direction="vertical">
              <span style={{ textDecoration: 'line-through', color: '#666', opacity: 0.8 }}>
                {originalValue}
              </span>
              <Box variant="awsui-key-label">
                {auditPreview.alias} - {auditPreview.content}
              </Box>
            </SpaceBetween>
          );
        }

        return (
          <SpaceBetween size="xs" direction="horizontal">
            <span style={{ textDecoration: 'line-through', color: '#666', opacity: 0.8 }}>
              {originalValue}
            </span>
            <span style={{ fontWeight: 'bold' }}>{newValue}</span>
          </SpaceBetween>
        );
      },
      minWidth: 100,
      maxWidth: 150,
    };
  });

  return [idColumn, ...changedValueColumns];
};
